import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Clock, FileCheck2, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/brand";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin");
    else if (user && user.role === "creative") {
      if (user.status === "approved") navigate("/portal");
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#050505] text-white grid-noise">
      {/* Nav */}
      <header className="border-b border-[#2A2A2A]">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 py-5 flex items-center justify-between gap-6">
          <Link to="/"><img src={LOGO_URL} alt="Splash Creative Hub" className="w-[140px] h-auto object-contain" /></Link>
          <div className="flex items-center gap-3">
            <Link data-testid="nav-status-link" to="/status" className="text-sm text-[#A1A1AA] hover:text-white transition-colors px-3 py-2">Check status</Link>
            <Link data-testid="nav-login-link" to="/login" className="text-sm text-[#A1A1AA] hover:text-white transition-colors px-3 py-2">Sign in</Link>
            <Link data-testid="nav-apply-link" to="/apply" className="text-sm bg-[#FF4D00] text-white px-4 py-2 hover:bg-[#E64500] transition-colors">Apply to the Team</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-8 lg:px-12 pt-20 pb-16 grid lg:grid-cols-12 gap-10 items-end">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-8">
          <div className="text-xs uppercase tracking-[0.25em] text-[#FF4D00] mb-6">The Splash Team · For Creatives</div>
          <h1 className="font-bebas text-6xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight">
            AN ELITE COMMUNITY OF<br /><span className="text-[#FF4D00]">CREATIVE PROS</span>
          </h1>
          <p className="mt-8 text-[#A1A1AA] text-base max-w-xl leading-relaxed">
            Splash is an invite-worthy collective of vetted freelance directors, cinematographers, editors, photographers and more. Get matched to premium jobs, then manage your entire workflow — assigned work, timesheets, and invoices tracked from pending to paid.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link data-testid="hero-apply-btn" to="/apply" className="group inline-flex items-center gap-2 bg-[#FF4D00] text-white px-7 py-4 hover:bg-[#E64500] transition-colors">
              Apply to the Team <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link data-testid="hero-signin-btn" to="/login" className="inline-flex items-center gap-2 border border-[#2A2A2A] px-7 py-4 hover:bg-[#111111] transition-colors">Sign in</Link>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="lg:col-span-4 border border-[#2A2A2A] aspect-[3/4] overflow-hidden hidden lg:block">
          <img src="https://images.unsplash.com/photo-1587050265310-1a2d98ccce5f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Cinematographer operating a cinema camera" className="w-full h-full object-cover grayscale contrast-125" />
        </motion.div>
      </section>

      {/* Feature grid */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        <div className="grid md:grid-cols-3 border-t border-l border-[#2A2A2A]">
          {[
            { icon: ShieldCheck, t: "Vetted Community", d: "Every creative is reviewed by hand. You're joining a curated roster, not an open marketplace." },
            { icon: Briefcase, t: "Premium Jobs", d: "See scope, due dates, and downloadable assets for every job matched to your craft." },
            { icon: Clock, t: "Timesheets", d: "Log start and end times per project. Admins review and approve every entry." },
            { icon: FileCheck2, t: "Invoice Tracking", d: "Upload invoice PDFs and follow their journey from Pending to Approved to Paid." },
            { icon: FileCheck2, t: "Guided Onboarding", d: "Simple, secure onboarding for your W-9, Non-Compete, and Conduct docs — no password needed to start." },
            { icon: ArrowRight, t: "Built To Move", d: "A brutalist, high-contrast interface engineered for speed and clarity." },
          ].map((f, i) => (
            <div key={i} className="border-r border-b border-[#2A2A2A] p-8 hover:bg-[#111111] transition-colors group">
              <f.icon className="w-7 h-7 text-[#FF4D00] mb-5" />
              <h3 className="font-bebas text-2xl tracking-wide mb-2">{f.t}</h3>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 border border-[#2A2A2A] bg-gradient-to-br from-[#1a0f08] to-[#0A0A0A] p-10 lg:p-14 text-center">
          <div className="font-bebas text-4xl sm:text-5xl tracking-wide mb-4">READY TO JOIN THE SPLASH TEAM?</div>
          <p className="text-sm text-[#A1A1AA] max-w-xl mx-auto mb-8">Apply in minutes — name, expertise, reel and resume. We review every application by hand and reach out when there's a fit.</p>
          <Link data-testid="bottom-apply-btn" to="/apply" className="group inline-flex items-center gap-2 bg-[#FF4D00] text-white px-8 py-4 hover:bg-[#E64500] transition-colors">
            Apply to the Team <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <p className="mt-6 text-xs text-[#555] max-w-xl mx-auto leading-relaxed">Your privacy is our priority — all documents are encrypted at rest and in transit, with access strictly limited to authorized administrators for payroll and compliance only.</p>
        </div>
      </section>

      <footer className="border-t border-[#2A2A2A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#A1A1AA]">
          <span className="font-bebas text-xl text-white tracking-wide">SPLASH CREATIVE HUB</span>
          <div className="flex items-center gap-5">
            <Link data-testid="footer-privacy-link" to="/privacy" className="hover:text-white transition-colors">Privacy &amp; Security</Link>
            <span>© 2026 Splash Creative Hub. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
